import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { getSeoMeta } from '../../services/content.service'

// Shown only if the API can't be reached; the same text index.html used to carry.
const FALLBACK = {
  title: 'Rayoptics - See the World in Style',
  description: 'Premium frames, lenses & contact lenses at budget-friendly prices with AI virtual try-on technology.',
  robots: 'index, follow',
  og: { type: 'website', title: 'Rayoptics - See the World in Style', description: 'Premium frames, lenses & contact lenses at budget-friendly prices with AI virtual try-on technology.', site_name: 'Rayoptics' },
  twitter: { card: 'summary' },
  jsonld: [],
}

// Only these query parameters change what a /shop page is "about"; sorting and filters share the category's SEO.
const SEO_PARAMS = ['category', 'sub_category', 'collection', 'search']

/**
 * Keeps <title>, meta tags, canonical link and structured data in step with the current URL.
 * The API decides all of it (see backend/app/services/seo.py) so the dashboard preview, this page and the
 * crawler-facing HTML can't drift apart.
 */
export default function Seo() {
  const { pathname, search } = useLocation()
  const params = new URLSearchParams(search)
  const query = {}
  for (const k of SEO_PARAMS) if (params.get(k)) query[k] = params.get(k)

  const { data, isError } = useQuery({
    queryKey: ['seo', pathname, query],
    queryFn: () => getSeoMeta(pathname, query),
    staleTime: 60_000,
    retry: 0,
  })
  const head = data || (isError ? FALLBACK : null)
  if (!head) return null // until it arrives, index.html's own title stays

  const { og, twitter } = head
  return (
    <Helmet>
      <title>{head.title}</title>
      <meta name="description" content={head.description} />
      <meta name="robots" content={head.robots} />
      {head.canonical && <link rel="canonical" href={head.canonical} />}
      <meta property="og:type" content={og.type} />
      <meta property="og:title" content={og.title} />
      <meta property="og:description" content={og.description} />
      {og.url && <meta property="og:url" content={og.url} />}
      {og.site_name && <meta property="og:site_name" content={og.site_name} />}
      {og.locale && <meta property="og:locale" content={og.locale} />}
      {og.image && <meta property="og:image" content={og.image} />}
      {og.article?.published_time && <meta property="article:published_time" content={og.article.published_time} />}
      {og.article?.modified_time && <meta property="article:modified_time" content={og.article.modified_time} />}
      {og.article?.author && <meta property="article:author" content={og.article.author} />}
      {(og.article?.tags || []).map((t) => <meta key={`tag-${t}`} property="article:tag" content={t} />)}
      {og.price && <meta property="product:price:amount" content={og.price.amount} />}
      {og.price && <meta property="product:price:currency" content={og.price.currency} />}
      <meta name="twitter:card" content={twitter.card} />
      {twitter.title && <meta name="twitter:title" content={twitter.title} />}
      {twitter.description && <meta name="twitter:description" content={twitter.description} />}
      {twitter.image && <meta name="twitter:image" content={twitter.image} />}
      {twitter.site && <meta name="twitter:site" content={twitter.site} />}
      {head.jsonld.map((block, i) => (
        <script key={`${pathname}-${i}`} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  )
}
